import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, Center, Loader, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { DEV_SWITCHER_OFFSET } from '@/components/dev-user-switcher';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import type { ApiData } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';

export const Route = createFileRoute('/invitations/$token')({
  component: InvitationPage,
});

type Invitation = ApiData<ReturnType<typeof api.invitations>['get']>;

function InvitationFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      style={{
        minHeight: `calc(100dvh - ${DEV_SWITCHER_OFFSET})`,
        background: 'var(--cd-signin-gradient)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--mantine-spacing-md)',
      }}
    >
      <Paper radius="lg" p="xl" shadow="lg" maw={420} w="100%">
        {children}
      </Paper>
    </Box>
  );
}

function SignInToAccept({ invitation }: { invitation: Invitation }) {
  return (
    <Stack gap="md">
      <Title order={2}>Join {invitation.organizationName}</Title>
      <Text c="dimmed" size="sm">
        An account already exists for <strong>{invitation.email}</strong>. Sign in to accept the invitation.
      </Text>
      <Button component={Link} to="/sign-in" fullWidth>
        Log in to accept
      </Button>
    </Stack>
  );
}

function CreateAccountForm({ invitation, token }: { invitation: Invitation; token: string }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: { firstName: '', lastName: '', password: '' },
    validate: {
      firstName: (value) => (value.trim() ? null : 'First name is required'),
      lastName: (value) => (value.trim() ? null : 'Last name is required'),
      password: (value) => (value.length >= 8 ? null : 'Password must be at least 8 characters'),
    },
  });

  const submit = form.onSubmit(async (values) => {
    setIsSubmitting(true);
    setError(null);

    const { error: apiError } = await api.invitations({ token }).accept.post(values);

    setIsSubmitting(false);

    if (apiError) {
      setError(getErrorMessage(apiError, 'Unable to accept this invitation'));

      return;
    }

    await navigate({ to: '/sign-in' });
  });

  return (
    <form onSubmit={submit}>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>Join {invitation.organizationName}</Title>
          <Text c="dimmed" size="sm">
            Create your CallDominator account for <strong>{invitation.email}</strong>.
          </Text>
        </Stack>
        <ErrorAlert message={error} />
        <TextInput label="First name" required {...form.getInputProps('firstName')} />
        <TextInput label="Last name" required {...form.getInputProps('lastName')} />
        <PasswordInput label="Password" required {...form.getInputProps('password')} />
        <Button type="submit" fullWidth loading={isSubmitting}>
          Accept invitation
        </Button>
      </Stack>
    </form>
  );
}

function InvitationPage() {
  const { token } = Route.useParams();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api
      .invitations({ token })
      .get()
      .then(({ data, error: apiError }) => {
        if (cancelled) return;

        if (apiError) setError(getErrorMessage(apiError, 'This invitation is no longer valid'));
        else setInvitation(data);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (error) {
    return (
      <InvitationFrame>
        <Stack gap="md">
          <Title order={2}>Invitation unavailable</Title>
          <ErrorAlert message={error} />
          <Button component={Link} to="/sign-in" variant="subtle" fullWidth>
            Go to sign in
          </Button>
        </Stack>
      </InvitationFrame>
    );
  }

  if (!invitation) {
    return (
      <InvitationFrame>
        <Center py="xl">
          <Loader />
        </Center>
      </InvitationFrame>
    );
  }

  return (
    <InvitationFrame>
      {invitation.hasAccount ? (
        <SignInToAccept invitation={invitation} />
      ) : (
        <CreateAccountForm invitation={invitation} token={token} />
      )}
    </InvitationFrame>
  );
}
